<template>
   <b-container>
      <div>
         <!-- Header -->
         <div class="mt-4 d-flex flex-row justify-content-between" style="width: 100%">
            <h1 style="color: black; font-weight: 600 !important">{{ companyName }}</h1>
            <div style="width: 500px">
               <StudySelect
                  class="mb-3"
                  allow-none
                  :selectedStudy="selectedStudy"
                  @studyChange="onStudyChange"
               />
            </div>
         </div>

         <!-- Calculation Models -->
         <div class="mt-5">
            <label class="mb-4 calc-title">Calculation Models</label>

            <b-row v-if="!isEmpty">
               <b-col cols="6" v-for="(chunk, cIndex) in groupedList" :key="cIndex">
                  <div
                     v-for="period in chunk"
                     :key="period.studyPeriodId"
                     class="mb-4 d-flex flex-row justify-content-between"
                     style="gap: 2rem; border-bottom: 1px solid lightgray"
                  >
                     <label class="font-weight-bold" style="font-size: 13px; color: black">
                        {{ getStudyLabel(period.studyPeriodId) }}
                     </label>

                     <div class="d-flex flex-column">
                        <div
                           v-for="(model, mIndex) in period.models"
                           :key="`${period.studyPeriodId}-${mIndex}`"
                           class="d-flex align-items-center mb-2 flex-row"
                        >
                           <b-form-checkbox
                              v-model="selectedModelKeys"
                              :value="buildSelectionKey(period.studyPeriodId, model)"
                              class="mr-2"
                           >
                              <span style="font-size: 13px">
                                 {{ model.label }}
                                 <small
                                    >(start date: {{ model.startDate
                                    }}<template v-if="model.expirationDate">
                                       – expires: {{ model.expirationDate }}</template
                                    >)</small
                                 >
                              </span>
                           </b-form-checkbox>
                        </div>
                     </div>
                  </div>
               </b-col>
            </b-row>

            <div v-else class="mt-3 text-muted">No calculation models available.</div>

            <b-button
               variant="primary"
               class="mt-3 mb-3"
               :disabled="
                  !selectedModelKeys || !selectedModelKeys.length || calculating || !selectedStudy
               "
               @click="calculate"
               :style="
                  !selectedModelKeys || !selectedModelKeys.length || calculating || !selectedStudy
                     ? 'background-color: lightgrey; border-color: lightgrey;'
                     : 'background-color: #1583c6'
               "
            >
               <span v-if="!calculating">Calculate</span>
               <b-spinner small v-else />
            </b-button>

            <!-- Preview -->
            <div v-if="calcResults && calcResults.length && selectedStudy" class="mt-5">
               <label class="mb-4 font-weight-bold" style="font-size: 20px">Preview</label>

               <b-tabs content-class="mt-3" v-model="activeYearTab">
                  <b-tab
                     v-for="(group, index) in resultsByYear"
                     :title="getStudyLabel(group.studyPeriodId)"
                     :key="index"
                  >
                     <div
                        style="
                           overflow-x: auto;
                           display: flex;
                           flex-wrap: nowrap;
                           gap: 1rem;
                           padding-bottom: 0.5rem;
                        "
                     >
                        <div
                           v-for="(result, rIndex) in group.results"
                           :key="rIndex"
                           class="mb-4"
                           :style="panelStyle(group.results.length)"
                        >
                           <h6 class="font-weight-bold mt-2 mb-2" style="font-size: 13px">
                              {{ result.modelName.trim() }}
                           </h6>

                           <b-table
                              v-if="result.lines && result.lines.length > 0"
                              :items="
                                 result.lines.filter(
                                    (l) =>
                                       ![
                                          'name',
                                          'identifying_number',
                                          'a_yes',
                                          'a_no',
                                          'b_yes',
                                          'b_no',
                                          '17_yes',
                                          '17_no',
                                          '34_yes',
                                          '34_no',
                                       ].includes(l.id)
                                 )
                              "
                              :fields="tableFields"
                              small
                              responsive
                              class="mb-3 horizontal-only-table"
                              :style="
                                 group.results.length > 1
                                    ? 'table-layout: fixed; max-width: 550px'
                                    : 'table-layout: fixed;'
                              "
                           >
                              <template #cell(value)="data" style="font-size: 13px">
                                 {{ formatValue(data.item.value) }}
                              </template>
                              <template #cell(line)="data">
                                 <div
                                    style="
                                       display: flex;
                                       gap: 0.5em;
                                       white-space: normal;
                                       word-break: break-word;
                                    "
                                 >
                                    <span style="min-width: 4ch; flex-shrink: 0">{{
                                       data.item.id
                                    }}</span>
                                    <span>{{ data.item.description }}</span>
                                 </div>
                              </template>
                           </b-table>
                           <div v-if="result.error" class="mb-3 horizontal-only-table">
                              {{ result.error }}
                           </div>
                           <div
                              v-if="result.lines && result.lines.length === 0"
                              class="mt-3 text-muted"
                           >
                              No calculation models available.
                           </div>
                        </div>
                     </div>
                  </b-tab>
               </b-tabs>
            </div>
         </div>
      </div>
   </b-container>
</template>

<script>
import {mapGetters} from 'vuex';
import StudySelect from '../company/widgets/StudySelect.vue';

export default {
   components: {StudySelect},
   data() {
      return {
         calculationModelsList: null,
         // Store only primitive keys for selected checkboxes
         selectedModelKeys: [],
         selectedStudy: '',
         calcResults: null,
         calculating: false,
         activeYearTab: 0,
         tableFields: [
            {key: 'line', label: 'Line – Title'},
            {key: 'value', label: 'Value'},
         ],
      };
   },
   computed: {
      ...mapGetters({
         profile: 'profile',
         currentCompany: 'companies/currentCompany',
         openStudies: 'companies/openStudies',
         activeStudy: 'companies/activeStudy',
         activeStudyPeriods: 'companies/activeStudyPeriods',
      }),
      companyId() {
         return this.$route.params.id || this.profile.companyId;
      },
      companyName() {
         return this.currentCompany.companyName;
      },
      resultsByYear() {
         if (!this.calcResults) return [];
         return this.calcResults.map((group) => ({
            ...group,
            results: [...group.results].sort((a, b) =>
               (a.location ?? '').localeCompare(b.location ?? '')
            ),
         }));
      },
      groupedList() {
         if (!this.calculationModelsList) return [];
         const sorted = this.calculationModelsList.map((period) => ({
            ...period,
            models: [...period.models].sort((a, b) =>
               (a.location ?? '').localeCompare(b.location ?? '')
            ),
         }));
         const half = Math.ceil(sorted.length / 2);
         return [sorted.slice(0, half), sorted.slice(half)];
      },
      isEmpty() {
         if (!this.calculationModelsList) return true;
         return this.calculationModelsList.every((item) => item.models.length === 0);
      },

      /**
       * Backward‑compatible, derived structure:
       * turns primitive keys into {studyPeriodId, model} objects.
       * This replaces the old "selectedModels" array of objects.
       */
      selectedModels() {
         if (!this.calculationModelsList || !this.selectedModelKeys) return [];

         return this.selectedModelKeys
            .map((key) => {
               const [studyPeriodIdStr, modelKey] = key.split('|');
               const studyPeriodId = Number(studyPeriodIdStr);
               const period = this.calculationModelsList.find(
                  (p) => p.studyPeriodId === studyPeriodId
               );
               if (!period) return null;

               const model = period.models.find((m) => this.buildModelKey(m) === modelKey);
               if (!model) return null;

               return {studyPeriodId, model};
            })
            .filter(Boolean);
      },
   },
   methods: {
      panelStyle(count) {
         if (count === 1) return 'flex: 1 1 auto;';
         if (count >= 3) return 'flex: 0 0 45%; min-width: 45%;';
         return 'flex: 0 0 calc(50% - 0.5rem); min-width: calc(50% - 0.5rem);';
      },

      // -------- key helpers (stable primitive ids) --------
      buildModelKey(model) {
         // Prefer unique model.id if present, otherwise use label+startDate combo as fallback
         if (model && model.id != null) {
            return String(model.id);
         }
         const label = model && model.label ? model.label : '';
         const start = model && model.startDate ? model.startDate : '';
         return `${label}::${start}`;
      },
      buildSelectionKey(studyPeriodId, model) {
         return `${studyPeriodId}|${this.buildModelKey(model)}`;
      },

      // -------- localStorage helpers (per company + user + study) --------
      storageScope() {
         return `${this.companyId}_${this.profile.id}`;
      },
      storageKeySelectedModels(studyId) {
         return `selectedModels_${this.storageScope()}_${studyId}`;
      },
      storageKeySelectedStudy() {
         return `calcSelectedStudy_${this.storageScope()}`;
      },

      saveStudyState() {
         if (!this.selectedStudy) return;

         // Save only primitive keys for selected models
         localStorage.setItem(
            this.storageKeySelectedModels(this.selectedStudy),
            JSON.stringify(this.selectedModelKeys || [])
         );
      },

      // -------- calc results (persisted server-side, keyed by study period) --------
      async fetchCalcResults() {
         this.calcResults = null;

         if (!this.activeStudyPeriods || !this.activeStudyPeriods.length) return;

         try {
            this.calcResults = await this.$store.dispatch('creditCalculation/fetchCalcResults', {
               studyPeriodsIds: this.activeStudyPeriods.map((p) => p.id),
            });
         } catch (error) {
            this.calcResults = null;
         }
      },

      async restoreStudyState(studyId) {
         if (!studyId) return;

         const modelsRaw = localStorage.getItem(this.storageKeySelectedModels(studyId));

         this.selectedModelKeys = [];

         if (modelsRaw) {
            try {
               const parsed = JSON.parse(modelsRaw);

               // Backward compatibility: if old data is array of objects, convert to keys
               if (Array.isArray(parsed) && parsed.length && typeof parsed[0] === 'object') {
                  this.selectedModelKeys = parsed
                     .map((item) => {
                        if (!item || !item.model) return null;
                        const studyPeriodId = item.studyPeriodId ?? item.study_period_id;
                        return this.buildSelectionKey(studyPeriodId, item.model);
                     })
                     .filter(Boolean);
               } else {
                  // New format: just an array of primitive keys
                  this.selectedModelKeys = parsed || [];
               }
            } catch (e) {
               this.selectedModelKeys = [];
            }
         }

         await this.fetchCalcResults();
      },

      // -------- UI helpers --------
      getStudyLabel(studyPeriodId) {
         const study = this.activeStudyPeriods.find((s) => s.id === studyPeriodId);
         return study ? study.label : '';
      },
      formatValue(value) {
         if (typeof value === 'number') {
            return value.toLocaleString('en-US', {
               minimumFractionDigits: 2,
               maximumFractionDigits: 2,
            });
         }
         return value;
      },
      // -------- study change handler (called from StudySelect) --------
      async onStudyChange(studyId) {
         // 1. Persist previous study’s state (if any)
         this.saveStudyState();

         // 2. Update selectedStudy + Vuex
         this.selectedStudy = studyId;
         this.$store.commit('companies/setActiveStudy', studyId);
         sessionStorage.setItem('rdigActiveStudy', JSON.stringify(studyId));
         localStorage.setItem(this.storageKeySelectedStudy(), JSON.stringify(studyId));

         // 3. Load models for this study
         await this.loadData();

         // 4. Restore any previously saved data for this study
         await this.restoreStudyState(studyId);
      },

      // -------- API loading --------
      async loadData() {
         this.calculationModelsList = null;

         if (
            !this.activeStudy ||
            (this.activeStudyPeriods && this.activeStudyPeriods.length === 0)
         ) {
            this.calculationModelsList = null;
            return;
         }

         try {
            const response = await this.$store.dispatch(
               'creditCalculation/fetchCalculationModels',
               {
                  studyPeriodsIds: this.activeStudyPeriods.map((p) => p.id),
               }
            );
            this.calculationModelsList = response;
         } catch (error) {
            this.calculationModelsList = null;
         }
      },

      // -------- initialisation on mount --------
      async initializeData() {
         // 1. Restore the last calculation Study. Prefer the persisted (localStorage)
         //    value so it survives a logout, falling back to the in-session key.
         const savedStudy =
            localStorage.getItem(this.storageKeySelectedStudy()) ||
            sessionStorage.getItem('rdigActiveStudy');
         if (savedStudy) {
            this.selectedStudy = JSON.parse(savedStudy);
         }

         // 2. Sync Vuex activeStudy (or fallback to first open study)
         if (this.selectedStudy) {
            this.$store.commit('companies/setActiveStudy', this.selectedStudy);
            sessionStorage.setItem('rdigActiveStudy', JSON.stringify(this.selectedStudy));
            localStorage.setItem(
               this.storageKeySelectedStudy(),
               JSON.stringify(this.selectedStudy)
            );
         } else if (this.openStudies && this.openStudies.length > 0) {
            this.selectedStudy = this.openStudies[0].id;
            this.$store.commit('companies/setActiveStudy', this.selectedStudy);
            sessionStorage.setItem('rdigActiveStudy', JSON.stringify(this.selectedStudy));
            localStorage.setItem(
               this.storageKeySelectedStudy(),
               JSON.stringify(this.selectedStudy)
            );
         }

         // 3. Wait so that activeStudyPeriods reflect the mutation above
         await this.$nextTick();

         // 4. Load models
         await this.loadData();

         // 5. Restore state (selectedModelKeys, calcResults) for the initial study
         await this.restoreStudyState(this.selectedStudy);
      },

      // -------- calculate action --------
      async calculate() {
         const models = this.selectedModels; // derived from keys
         if (!models || !models.length) return;

         this.calculating = true;

         const grouped = models.reduce((acc, {studyPeriodId, model}) => {
            const existing = acc.find((x) => x.studyPeriodId === studyPeriodId);
            if (existing) existing.models.push(model);
            else acc.push({studyPeriodId, models: [model]});
            return acc;
         }, []);

         try {
            const response = await this.$store.dispatch('creditCalculation/calculateModels', {
               models: grouped,
            });
            this.calcResults = response;

            // Persist state for the current study
            this.saveStudyState();
         } catch (error) {
            this.calcResults = null;
         } finally {
            this.calculating = false;
         }
      },
   },
   async mounted() {
      await this.initializeData();
   },
};
</script>

<style lang="scss">
.calc-title {
   color: grey;
   font-size: 12px !important;
}

.horizontal-only-table.table td,
.horizontal-only-table.table th {
   border: none;
   border-top: 1px solid #dee2e6;
}

.nav-tabs .nav-link.active,
.nav-tabs .nav-item.show .nav-link {
   background-color: white !important;
   border-bottom: 3px solid dodgerblue !important;
   color: black !important;
}

.nav-tabs .nav-link {
   background-color: white !important;
   color: black !important;
}

.nav-tabs {
   border-bottom: 1px solid lightgray !important;
}
</style>
